import { useState, useEffect } from 'react';
import { contactService } from '../../services/contactService';
import { organizationService } from '../../services/organizationService';
import type { Contact, CreateContactRequest, UpdateContactRequest, ContactType } from '../../types/contact';
import { useConfirm } from '../common/components/useConfirm';
import { MIN_SEARCH_LENGTH } from '../../utils/searchConstants';

interface UseContactDataParams {
  companyId: number | null;
  typeFilter?: ContactType;
}

export function useContactData({ companyId, typeFilter }: UseContactDataParams) {
  const { confirm, confirmDialog } = useConfirm();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizationNamesById, setOrganizationNamesById] = useState<Map<number, string>>(new Map());

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterName, setFilterName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [debouncedPhone, setDebouncedPhone] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [filterType, setFilterType] = useState<ContactType | undefined>(undefined);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);

  // Debounce filter
  useEffect(() => {
    if (filterName.length > 0 && filterName.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => {
      setDebouncedName(filterName);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName]);

  useEffect(() => {
    if (filterPhone.length > 0 && filterPhone.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => {
      setDebouncedPhone(filterPhone);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterPhone]);

  // Fetch contacts
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await contactService.search(companyId, {
          type: typeFilter ?? filterType,
          search: debouncedName || undefined,
          phone: debouncedPhone || undefined,
          includeArchived: !showOnlyActive,
          page: page - 1,
          size: 20,
        });
        if (cancelled) {
          return;
        }
        setContacts(response.content);
        setTotalPages(response.totalPages);
        setError(null);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        if (!cancelled) {
          setError('Не вдалося завантажити контакти');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setIsFirstLoad(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [companyId, page, debouncedName, debouncedPhone, typeFilter, filterType, showOnlyActive]);

  // Organizations for the whole company are loaded once and looked up locally per row,
  // same rationale as OrganizationAutocomplete — few organizations per company expected.
  useEffect(() => {
    if (!companyId) {
      return;
    }
    let cancelled = false;
    organizationService.listAllActive(companyId)
      .then((organizations) => {
        if (cancelled) {
          return;
        }
        setOrganizationNamesById(new Map(organizations.map((o) => [o.id, o.name])));
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setOrganizationNamesById(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleCreateContact = async (data: CreateContactRequest) => {
    if (!companyId) return;
    const newContact = await contactService.create(data);
    setContacts((prev) => [...prev, newContact]);
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!(await confirm('Ви впевнені, що хочете архівувати цей контакт?'))) return;
    await contactService.delete(contactId);
    setContacts((prev) => (
      showOnlyActive
        ? prev.filter((c) => c.id !== contactId)
        : prev.map((c) => (c.id === contactId ? { ...c, deactivatedDate: new Date().toISOString() } : c))
    ));
  };

  const handleRestoreContact = async (contactId: number) => {
    await contactService.restore(contactId);
    setContacts((prev) => prev.map((c) => (
      c.id === contactId ? { ...c, deactivatedDate: undefined } : c
    )));
  };

  const handleEditClick = (contact: Contact) => {
    setContactToEdit(contact);
    setEditDialogOpen(true);
  };

  const handleUpdateContact = async (contactId: number, data: UpdateContactRequest) => {
    if (!companyId) return;
    const updated = await contactService.update(contactId, data);
    setContacts((prev) => prev.map((c) => (c.id === contactId ? updated : c)));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleShowOnlyActiveChange = (value: boolean) => {
    setShowOnlyActive(value);
    setPage(1);
  };

  const handleFilterTypeChange = (value: ContactType | undefined) => {
    setFilterType(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterName('');
    setFilterPhone('');
    handleFilterTypeChange(undefined);
    handleShowOnlyActiveChange(true);
  };

  return {
    data: {
      contacts,
      loading,
      isFirstLoad,
      error,
      organizationNamesById,
    },
    pagination: {
      page,
      count: totalPages,
      onChange: handlePageChange,
    },
    filters: {
      filterName,
      setFilterName,
      debouncedName,
      filterPhone,
      setFilterPhone,
      debouncedPhone,
      filterType,
      setFilterType: handleFilterTypeChange,
      showFilters,
      setShowFilters,
      showOnlyActive,
      setShowOnlyActive: handleShowOnlyActiveChange,
      handleClearFilters,
      hasActiveFilters: !!(filterName || filterPhone || filterType) || !showOnlyActive,
    },
    crud: {
      handleCreateContact,
      handleDeleteContact,
      handleRestoreContact,
      handleEditClick,
      handleUpdateContact,
    },
    dialogs: {
      createDialogOpen,
      setCreateDialogOpen,
      editDialogOpen,
      setEditDialogOpen,
      contactToEdit,
      setContactToEdit,
      confirmDialog,
    },
  };
}
